import { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { fallbackTopics } from "../lib/subjectTopics";

/**
 * Fetches topics from the backend filtered by subject + grade.
 *
 * When grade is set, topics come exclusively from the API (DB-backed) — no
 * fallback list is shown. When grade is absent, the dropdown is seeded from
 * a local mirror of SUBJECT_TOPICS while the API loads.
 */
export default function useSubjectTopics(subject, grade, { extra = [], defaultTopic = null } = {}) {
  const seed = subject && !grade ? [...extra, ...fallbackTopics(subject)] : [...extra];
  const [topics, setTopics] = useState(seed);
  const [topic, setTopic] = useState(defaultTopic || extra[0] || "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Reset topic the instant subject or grade changes so the UI never shows a stale value.
    setTopic(defaultTopic || extra[0] || "");
    
    // When grade is set, only show sentinels while loading — real topics come from the API.
    // When no grade, seed with all subject topics so the dropdown has content while loading.
    const seeded = subject && !grade ? [...extra, ...fallbackTopics(subject)] : [...extra];
    setTopics(seeded);

    if (!subject) return;
    let cancelled = false;
    setLoading(true);
    // URLSearchParams already percent-encodes values — do NOT encodeURIComponent here
    // or spaces in "Grade 9" get double-encoded and never match the DB.
    const params = new URLSearchParams({ subject });
    if (grade) params.set("grade", grade);
    apiClient.get(`/questions/topics?${params.toString()}`)
      .then((r) => {
        if (cancelled) return;
        const apiList = r.data?.topics || [];
        
        // When grade is set, use ONLY API results (DB-backed, no fallback)
        // When no grade, merge API + fallback for full subject coverage
        let merged;
        if (grade) {
          merged = Array.from(new Set([...extra, ...apiList]));
        } else {
          merged = Array.from(new Set([...seeded, ...apiList]));
        }
        
        const sorted = merged.sort((a, b) => {
          // keep `extra` items (e.g. "Mixed") at the very top in their original order
          const ai = extra.indexOf(a);
          const bi = extra.indexOf(b);
          if (ai !== -1 || bi !== -1) {
            if (ai === -1) return 1;
            if (bi === -1) return -1;
            return ai - bi;
          }
          return a.localeCompare(b);
        });
        setTopics(sorted);
        if (!sorted.includes(topic)) {
          setTopic(defaultTopic || extra[0] || sorted[0] || "");
        }
      })
      .catch(() => {
        // Keep the synchronous seed — the dropdown still works offline.
        if (!cancelled) setTopics(seeded);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // intentionally omitting `topic` from deps — we only want a refetch on subject or grade change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, grade]);

  return { topics, topic, setTopic, loading };
}
